import { useCallback, useEffect, useState } from "react";
import { api, apiErrorMessage } from "@/lib/api";
import { PageHeader } from "@/components/PageHelpers";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { RefreshCw, ShieldAlert, UserCog, Timer, MessageSquareWarning } from "lucide-react";
import { toast } from "sonner";

/**
 * Impersonation Session Log — R110.
 *
 * Consumes `/api/admin/impersonate/log` which pairs impersonation.start with
 * impersonation.end events into per-session rows so compliance reviewers see
 * one row per session with actor + target + reason + duration + status.
 */
function fmtDuration(seconds) {
  if (seconds == null) return "—";
  if (seconds < 60) return `${seconds}s`;
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  if (mins < 60) return `${mins}m${secs ? ` ${secs}s` : ""}`;
  const hrs = Math.floor(mins / 60);
  const remMins = mins % 60;
  return `${hrs}h${remMins ? ` ${remMins}m` : ""}`;
}

function StatusPill({ status }) {
  const tone = {
    active: "border-amber-500/40 text-amber-300 bg-amber-500/10",
    ended: "border-emerald-500/40 text-emerald-300 bg-emerald-500/10",
    expired: "border-zinc-500/40 text-zinc-400 bg-zinc-500/10",
  }[status] || "border-white/10";
  return <Badge variant="outline" className={tone + " text-[10px] font-semibold uppercase tracking-widest"} data-testid={`imp-status-${status}`}>{status}</Badge>;
}

export default function ImpersonationLogPage() {
  const [state, setState] = useState({ sessions: [], total_sessions: 0 });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/impersonate/log?limit=100");
      setState(data);
    } catch (e) {
      toast.error(apiErrorMessage(e) || "Failed to load impersonation log");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const active = state.sessions.filter((s) => s.status === "active");
  const noReasonCount = state.sessions.filter((s) => !s.reason).length;

  return (
    <div className="p-8 space-y-6" data-testid="impersonation-log-page">
      <PageHeader
        title="Impersonation session log"
        subtitle="Every admin walk-in to a resident/vendor account. Compliance officers use this to spot unusual access patterns and reconcile support tickets."
        actions={
          <Button variant="outline" onClick={load} className="border-white/10 gap-1 h-9" data-testid="imp-log-refresh">
            <RefreshCw className="h-3.5 w-3.5" /> Refresh
          </Button>
        }
      />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Card className="border-white/10 bg-white/[0.02] p-4">
          <div className="flex items-center gap-2 text-xs text-zinc-500 uppercase tracking-widest">
            <UserCog className="h-3.5 w-3.5" /> Sessions logged
          </div>
          <div className="text-3xl font-semibold mt-2 text-emerald-300" data-testid="imp-log-total">
            {state.total_sessions}
          </div>
        </Card>
        <Card className="border-white/10 bg-white/[0.02] p-4">
          <div className="flex items-center gap-2 text-xs text-zinc-500 uppercase tracking-widest">
            <Timer className="h-3.5 w-3.5" /> Active right now
          </div>
          <div className={"text-3xl font-semibold mt-2 " + (active.length ? "text-amber-300" : "text-zinc-300")}
                data-testid="imp-log-active-count">
            {active.length}
          </div>
        </Card>
        <Card className="border-white/10 bg-white/[0.02] p-4">
          <div className="flex items-center gap-2 text-xs text-zinc-500 uppercase tracking-widest">
            <MessageSquareWarning className="h-3.5 w-3.5" /> Sessions without reason
          </div>
          <div className={"text-3xl font-semibold mt-2 " + (noReasonCount ? "text-rose-300" : "text-emerald-300")}
                data-testid="imp-log-noreason-count">
            {noReasonCount}
          </div>
          <div className="text-[10px] text-zinc-500 mt-1">
            {noReasonCount ? "Older sessions — reason field added in R110" : "Every session has a documented reason"}
          </div>
        </Card>
      </div>

      <Card className="border-white/10 bg-white/[0.02]">
        {loading ? (
          <div className="p-10 text-center text-sm text-zinc-500">Loading log…</div>
        ) : state.sessions.length === 0 ? (
          <div className="p-10 text-center text-sm text-zinc-500 flex items-center justify-center gap-2">
            <ShieldAlert className="h-4 w-4 text-emerald-400" />
            No impersonation sessions recorded yet.
          </div>
        ) : (
          <div className="divide-y divide-white/5" data-testid="imp-log-list">
            {state.sessions.map((s) => (
              <div key={s.id} className="p-4 flex items-start gap-4 hover:bg-white/[0.02]" data-testid={`imp-row-${s.id}`}>
                <div className="mt-1 h-8 w-8 rounded-full bg-amber-500/10 border border-amber-500/30 flex items-center justify-center shrink-0">
                  <UserCog className="h-3.5 w-3.5 text-amber-300" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="text-sm font-semibold text-white truncate">
                      {s.actor_name || s.actor_id} <span className="text-zinc-500 text-[11px]">({s.actor_role})</span>
                    </div>
                    <span className="text-zinc-500">→</span>
                    <div className="text-sm text-zinc-300 truncate">
                      {s.target_email || s.target_id} <span className="text-zinc-500 text-[11px]">({s.target_role})</span>
                    </div>
                    <StatusPill status={s.status} />
                    {s.duration_seconds != null && (
                      <Badge variant="outline" className="border-white/10 text-zinc-300 text-[10px]" data-testid={`imp-duration-${s.id}`}>
                        {fmtDuration(s.duration_seconds)}
                      </Badge>
                    )}
                  </div>
                  <div className="text-xs text-zinc-500 mt-1 flex items-center flex-wrap gap-x-3">
                    <span>Started {new Date(s.started_at).toLocaleString()}</span>
                    {s.ended_at && <span>Ended {new Date(s.ended_at).toLocaleString()}</span>}
                  </div>
                  {s.reason ? (
                    <div className="mt-2 text-xs text-zinc-300 border-l-2 border-amber-500/40 pl-2 py-0.5" data-testid={`imp-reason-${s.id}`}>
                      <span className="text-[10px] uppercase tracking-widest text-zinc-500 mr-1">Reason:</span>
                      {s.reason}
                    </div>
                  ) : (
                    <div className="mt-2 text-xs text-rose-300/70 italic" data-testid={`imp-noreason-${s.id}`}>
                      No reason recorded (legacy session pre-R110)
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
